import { useSearchParams } from "react-router-dom";

export type ComparisonMode = "prev" | "week" | "month" | "year";
export type Preset = "today" | "yesterday" | "7d" | "30d" | "90d" | "this-month" | "custom";

export interface PeriodState {
  preset: Preset;
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
  vs: ComparisonMode;
}

const DEFAULT_PRESET: Preset = "30d";
const DEFAULT_VS: ComparisonMode = "prev";

function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function presetToRange(preset: Preset): { from: string; to: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  switch (preset) {
    case "today":
      return { from: fmtDate(today), to: fmtDate(today) };
    case "yesterday":
      return { from: fmtDate(yesterday), to: fmtDate(yesterday) };
    case "7d": {
      const start = new Date(yesterday);
      start.setDate(start.getDate() - 6);
      return { from: fmtDate(start), to: fmtDate(yesterday) };
    }
    case "30d": {
      const start = new Date(yesterday);
      start.setDate(start.getDate() - 29);
      return { from: fmtDate(start), to: fmtDate(yesterday) };
    }
    case "90d": {
      const start = new Date(yesterday);
      start.setDate(start.getDate() - 89);
      return { from: fmtDate(start), to: fmtDate(yesterday) };
    }
    case "this-month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: fmtDate(start), to: fmtDate(yesterday) };
    }
    case "custom":
      // не используется — для custom URL содержит явные from/to
      return { from: fmtDate(yesterday), to: fmtDate(yesterday) };
  }
}

export function usePeriodAndComparison(): {
  state: PeriodState;
  setPreset: (p: Preset) => void;
  setRange: (from: string, to: string) => void; // sets preset=custom + from/to
  setVs: (m: ComparisonMode) => void;
  apiQueryString: string; // ready-to-use for fetch: from=...&to=...&vs=...
} {
  const [params, setParams] = useSearchParams();

  const presetParam = (params.get("preset") as Preset | null);
  const fromParam = params.get("from");
  const toParam = params.get("to");
  const vs = (params.get("vs") ?? DEFAULT_VS) as ComparisonMode;

  let preset: Preset;
  let from: string;
  let to: string;

  if (fromParam && toParam && /^\d{4}-\d{2}-\d{2}$/.test(fromParam) && /^\d{4}-\d{2}-\d{2}$/.test(toParam)) {
    // явный диапазон в URL — это custom mode (если preset не задан) или результат preset (если preset задан)
    preset = presetParam ?? "custom";
    from = fromParam;
    to = toParam;
  } else if (presetParam) {
    preset = presetParam;
    const range = presetToRange(presetParam);
    from = range.from;
    to = range.to;
  } else {
    preset = DEFAULT_PRESET;
    const range = presetToRange(DEFAULT_PRESET);
    from = range.from;
    to = range.to;
  }

  function setPreset(p: Preset) {
    const next = new URLSearchParams(params);
    next.set("preset", p);
    if (p === "custom") {
      // оставляем существующие from/to в URL если есть, иначе ставим вчера
      if (!fromParam || !toParam) {
        const range = presetToRange("yesterday");
        next.set("from", range.from);
        next.set("to", range.to);
      }
    } else {
      // удаляем from/to чтобы preset был источником истины
      next.delete("from");
      next.delete("to");
    }
    setParams(next, { replace: true });
  }

  function setRange(newFrom: string, newTo: string) {
    const next = new URLSearchParams(params);
    next.set("preset", "custom");
    next.set("from", newFrom);
    next.set("to", newTo);
    setParams(next, { replace: true });
  }

  function setVs(m: ComparisonMode) {
    const next = new URLSearchParams(params);
    next.set("vs", m);
    setParams(next, { replace: true });
  }

  // строим api-query всегда из явных from/to — backend работает по этому пути
  const apiQueryString = `from=${from}&to=${to}&vs=${vs}`;

  return { state: { preset, from, to, vs }, setPreset, setRange, setVs, apiQueryString };
}
